import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import PostAdd from '@mui/icons-material/PostAdd';
import { JsonForms } from '@jsonforms/react';
import type { JsonSchema7, UISchemaElement } from '@jsonforms/core';
import { materialCells, materialRenderers } from '@jsonforms/material-renderers';
import { apiClient } from '@/lib/apiClient';
import { useHasPermission } from '@/components/PermissionGate';
import { customRenderers } from '@/components/forms/renderers';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { nexoJsonFormsAjv } from '@/lib/jsonFormsAjv';
import { useJsonFormsTranslation } from '@/hooks/useJsonFormsTranslation';
import { IntegrationJobCreateSchema } from '@/forms/integration/IntegrationJobSchema';
import { withDeclaredEndpoint, type DeclaredMessageType, type FromMessageTypeInput } from './messageTypeEndpoint';

const jobFields = IntegrationJobCreateSchema.properties as Record<string, JsonSchema7>;

/**
 * What the tenant decides; the job's own field schemas (labels, lookups) are reused as generated.
 * Endpoint, paths, verification, receiving entity and stylesheet or adapter come from the declaration.
 */
const schema: JsonSchema7 = {
  type: 'object',
  required: ['messageType', 'endpoint', 'name', 'integrationConnectionId'],
  properties: {
    messageType: {
      title: 'Message type',
      description: 'The declared inbound message type the channel receives',
      type: 'string',
      format: 'api-lookup',
      'x-lookup': {
        apiMode: true,
        endpoint: '/api/integration/integrationjob/message-types',
        valueField: 'name',
        displayField: 'displayName',
        minSearchLength: 0,
      },
    } as JsonSchema7,
    endpoint: jobFields.inboundMessageType,
    name: jobFields.name,
    integrationConnectionId: jobFields.integrationConnectionId,
    defaultOrgUnitId: jobFields.defaultOrgUnitId,
    acceptTestFlag: jobFields.acceptTestFlag,
  },
};

const uischema: UISchemaElement = {
  type: 'VerticalLayout',
  elements: ['messageType', 'endpoint', 'name', 'integrationConnectionId', 'defaultOrgUnitId', 'acceptTestFlag']
    .map((field) => ({ type: 'Control', scope: `#/properties/${field}` })),
} as UISchemaElement;

/**
 * "New from message type" on the Integration Jobs list: creates a push job as a copy of a
 * declared inbound message type (adapter DSL kind: xslt). The dialog asks only what the tenant
 * decides — name, connection, legal entity, test flag; the server copies the rest from the
 * declaration, and the new job opens for review and editing.
 */
export function NewJobFromMessageTypeButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useHasPermission('Integration.IntegrationJob.Create');
  const jsonFormsI18n = useJsonFormsTranslation('IntegrationJob');
  const renderers = useMemo(() => [...materialRenderers, ...customRenderers], []);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<FromMessageTypeInput>({ acceptTestFlag: false });
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);
  const { data: types = [] } = useQuery({
    queryKey: ['integration-job-message-types'],
    queryFn: async () =>
      (await apiClient.get<DeclaredMessageType[]>('/api/integration/integrationjob/message-types')).data,
    enabled: open,
  });

  const create = useMutation({
    mutationFn: async (input: FromMessageTypeInput) =>
      (await apiClient.post<{ id: string }>('/api/integration/integrationjob/from-message-type', input)).data,
    onSuccess: (job) => {
      setOpen(false);
      navigate(`/integration/integrationjobs/${job.id}`);
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
        return;
      }
      setApiValidationErrors([{ field: 'form', message: 'An error occurred', instancePath: '#', members: [] }]);
    },
  });

  if (!canCreate) return null;

  const complete = !!data.messageType && !!data.endpoint?.trim() && !!data.name?.trim() && !!data.integrationConnectionId;
  const close = () => {
    setOpen(false);
    setApiValidationErrors([]);
  };

  return (
    <>
      <Button
        startIcon={<PostAdd />}
        onClick={() => {
          setData({ acceptTestFlag: false });
          setApiValidationErrors([]);
          setOpen(true);
        }}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('Integration.IntegrationJob.NewFromMessageType', { defaultValue: 'New from message type' })}
      </Button>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('Integration.IntegrationJob.NewFromMessageType', { defaultValue: 'New from message type' })}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('Integration.IntegrationJob.NewFromMessageTypeHint', {
              defaultValue:
                'Message id and key paths, verification, receiving entity and stylesheet or adapter are copied from the message type, and its endpoint unless you name another. The job is yours to edit afterwards.',
            })}
          </Typography>
          <ValidationErrorBanner
            errors={apiValidationErrors}
            onExpand={() => setValidationPanelOpen(true)}
            onClear={() => setApiValidationErrors([])}
          />
          <JsonFormsThemeProvider density="compact" variant="standard">
            <JsonForms
              schema={schema}
              uischema={uischema}
              data={data}
              renderers={renderers}
              cells={materialCells}
              ajv={nexoJsonFormsAjv}
              i18n={jsonFormsI18n}
              validationMode="NoValidation"
              onChange={({ data: next }) =>
                setData((previous) => withDeclaredEndpoint(previous, next as FromMessageTypeInput, types))}
            />
          </JsonFormsThemeProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          <Button variant="contained" disabled={!complete || create.isPending} onClick={() => create.mutate(data)}>
            {t('common.create', { defaultValue: 'Create' })}
          </Button>
        </DialogActions>
      </Dialog>
      <ValidationErrorPanel
        errors={apiValidationErrors}
        open={validationPanelOpen}
        onClose={() => setValidationPanelOpen(false)}
      />
    </>
  );
}
